import { useState } from 'react';
import { X, Save, Target, MapPin, Users, Calendar, Heart, TrendingUp, Plus, Trash2, Globe, Smartphone, Layout, Ban, Upload, Download } from 'lucide-react';

interface CityLocation {
  key: string;
  name: string;
  country_code: string;
  radius?: number;
  distance_unit?: 'kilometer' | 'mile';
}

interface CustomAudience {
  id: string;
  name: string;
}

interface TargetingConfig {
  age_min: number;
  age_max: number;
  genders: number[];
  geo_locations: {
    countries: string[];
    cities?: CityLocation[];
  };
  locales?: string[];
  interests?: { id: string; name: string }[];
  behaviors?: { id: string; name: string }[];
  excluded_interests?: { id: string; name: string }[];
  excluded_behaviors?: { id: string; name: string }[];
  custom_audiences?: CustomAudience[];
  device_platforms?: string[];
  publisher_platforms?: string[];
  facebook_positions?: string[];
  instagram_positions?: string[];
}

interface AdvancedTargetingModalProps {
  phase: 1 | 2;
  initialTargeting: TargetingConfig;
  onClose: () => void;
  onSave: (targeting: TargetingConfig) => void;
}

const COUNTRIES = [
  { code: 'PL', name: 'Polska' },
  { code: 'US', name: 'Stany Zjednoczone' },
  { code: 'GB', name: 'Wielka Brytania' },
  { code: 'DE', name: 'Niemcy' },
  { code: 'FR', name: 'Francja' },
  { code: 'IT', name: 'Włochy' },
  { code: 'ES', name: 'Hiszpania' },
  { code: 'CZ', name: 'Czechy' },
  { code: 'SK', name: 'Słowacja' },
  { code: 'UA', name: 'Ukraina' },
];

const LANGUAGES = [
  { code: 'pl_PL', name: 'Polski' },
  { code: 'en_US', name: 'English (US)' },
  { code: 'en_GB', name: 'English (UK)' },
  { code: 'de_DE', name: 'Deutsch' },
  { code: 'fr_FR', name: 'Français' },
  { code: 'es_ES', name: 'Español' },
  { code: 'it_IT', name: 'Italiano' },
  { code: 'cs_CZ', name: 'Čeština' },
  { code: 'sk_SK', name: 'Slovenčina' },
  { code: 'uk_UA', name: 'Українська' },
];

const LOCALE_MAP: Record<string, number> = {
  'pl_PL': 15, 'en_US': 6, 'en_GB': 24, 'de_DE': 5, 'fr_FR': 9, 'es_ES': 7, 'it_IT': 10, 'cs_CZ': 2, 'sk_SK': 33, 'uk_UA': 52
};
const REVERSE_LOCALE_MAP: Record<number, string> = Object.fromEntries(Object.entries(LOCALE_MAP).map(([k, v]) => [v, k]));

const POPULAR_INTERESTS = [
  { id: '6003371567474', name: 'Biznes i Przedsiębiorczość' },
  { id: '6003279598823', name: 'Marketing' },
  { id: '6003985771306', name: 'Technologia' },
  { id: '6003221485467', name: 'E-commerce' },
  { id: '6003389760112', name: 'Social Media Marketing' },
  { id: '6003384248805', name: 'Fitness i Zdrowie' },
  { id: '6004160395895', name: 'Podróże' },
  { id: '6002867432822', name: 'Moda i Uroda' },
  { id: '6003659420716', name: 'Jedzenie i Gotowanie' },
  { id: '6003269553527', name: 'Sport' },
  { id: '6003139266461', name: 'Film i Kino' },
  { id: '6003057392644', name: 'Gaming' },
  { id: '6003462707303', name: 'Książki i Literatura' },
  { id: '6003578086487', name: 'Nieruchomości' },
  { id: '6003143720966', name: 'Finanse Osobiste' },
  { id: '6003109292833', name: 'Dropshipping' },
  { id: '6003230166788', name: 'Shopify' },
  { id: '6003287729076', name: 'Dochód Pasywny' },
  { id: '6002884511422', name: 'Mały Biznes' },
  { id: '6003020834693', name: 'Muzyka' },
];

const POPULAR_BEHAVIORS = [
  { id: '6071631541183', name: 'Aktywni Kupujący Online' },
  { id: '6273196847983', name: 'Nowy Aktywny Biznes (<12 mies.)' },
  { id: '6273108079183', name: 'Nowy Aktywny Biznes (<6 mies.)' },
  { id: '6297846662583', name: 'Admini Profili Biznesowych IG' },
  { id: '6377178995383', name: 'Admini Sklepów' },
  { id: '6041891177783', name: 'Nowi Admini Stron' },
];

const INTERNATIONAL_CITIES = {
  PL: [
    { key: '274892', name: 'Warszawa', country_code: 'PL' },
    { key: '277456', name: 'Kraków', country_code: 'PL' },
    { key: '277841', name: 'Wrocław', country_code: 'PL' },
    { key: '278412', name: 'Poznań', country_code: 'PL' },
    { key: '276679', name: 'Gdańsk', country_code: 'PL' },
    { key: '275999', name: 'Łódź', country_code: 'PL' },
    { key: '279261', name: 'Katowice', country_code: 'PL' },
    { key: '278945', name: 'Szczecin', country_code: 'PL' },
  ],
  US: [
    { key: '2490299', name: 'New York', country_code: 'US' },
    { key: '2418779', name: 'Los Angeles', country_code: 'US' },
    { key: '2419355', name: 'Chicago', country_code: 'US' },
    { key: '2430683', name: 'Miami', country_code: 'US' },
    { key: '2471217', name: 'San Francisco', country_code: 'US' },
  ],
  GB: [
    { key: '2430536', name: 'London', country_code: 'GB' },
    { key: '2520994', name: 'Manchester', country_code: 'GB' },
    { key: '2512363', name: 'Birmingham', country_code: 'GB' },
  ],
  DE: [
    { key: '2950159', name: 'Berlin', country_code: 'DE' },
    { key: '2867714', name: 'Munich', country_code: 'DE' },
    { key: '2886242', name: 'Frankfurt', country_code: 'DE' },
    { key: '2911298', name: 'Hamburg', country_code: 'DE' },
  ],
};

const DEVICE_PLATFORMS = [
  { value: 'mobile', label: 'Mobile (iOS + Android)' },
  { value: 'desktop', label: 'Desktop' },
  { value: 'tablet', label: 'Tablet' },
];

const FACEBOOK_POSITIONS = [
  { value: 'feed', label: 'Facebook Feed' },
  { value: 'marketplace', label: 'Facebook Marketplace' },
  { value: 'story', label: 'Facebook Stories' },
];

const INSTAGRAM_POSITIONS = [
  { value: 'stream', label: 'Instagram Feed' },
  { value: 'story', label: 'Instagram Stories' },
  { value: 'explore', label: 'Instagram Explore' },
  { value: 'reels', label: 'Instagram Reels' },
];

export default function AdvancedTargetingModal({
  phase,
  initialTargeting,
  onClose,
  onSave,
}: AdvancedTargetingModalProps) {
  const [targeting, setTargeting] = useState<TargetingConfig>(() => {
    // Reconstruct UI state from Meta API format
    const behaviors = initialTargeting.behaviors || 
      (initialTargeting.flexible_spec?.find((spec: any) => spec.behaviors)?.behaviors) || [];
      
    const excluded_behaviors = initialTargeting.excluded_behaviors || 
      initialTargeting.exclusions?.behaviors || [];
      
    const excluded_interests = initialTargeting.excluded_interests || 
      initialTargeting.exclusions?.interests || [];

    return {
      ...initialTargeting,
      interests: initialTargeting.interests || [],
      behaviors,
      excluded_interests,
      excluded_behaviors,
      locales: (initialTargeting.locales || []).map((l: any) => typeof l === 'number' ? REVERSE_LOCALE_MAP[l] : l).filter((l: any) => l),
      custom_audiences: initialTargeting.custom_audiences || [],
    device_platforms: initialTargeting.device_platforms || ['mobile', 'desktop'],
    publisher_platforms: initialTargeting.publisher_platforms || ['facebook', 'instagram'],
    facebook_positions: initialTargeting.facebook_positions || [],
    instagram_positions: initialTargeting.instagram_positions || [],
    geo_locations: {
      ...initialTargeting.geo_locations,
      cities: initialTargeting.geo_locations?.cities || [],
    }
    };
  });

  const [activeTab, setActiveTab] = useState<'basic' | 'advanced' | 'exclusions' | 'placement'>('basic');
  const [citySearch, setCitySearch] = useState('');
  const [interestSearch, setInterestSearch] = useState('');
  const [selectedRadius, setSelectedRadius] = useState(25);
  const [selectedCountry, setSelectedCountry] = useState('PL');

  const handleSave = () => {
    const finalTargeting: any = { ...targeting };
    
    // Locales mapping
    if (finalTargeting.locales && finalTargeting.locales.length > 0) {
      finalTargeting.locales = finalTargeting.locales
        .map((l: any) => LOCALE_MAP[l])
        .filter((l: any) => l !== undefined && l !== null);
    } else {
      delete finalTargeting.locales;
    }

    // Exclusions mapping (Meta API expects exclusions object)
    const hasExcludedBehaviors = finalTargeting.excluded_behaviors && finalTargeting.excluded_behaviors.length > 0;
    const hasExcludedInterests = finalTargeting.excluded_interests && finalTargeting.excluded_interests.length > 0;
    
    if (hasExcludedBehaviors || hasExcludedInterests) {
      finalTargeting.exclusions = {};
      if (hasExcludedBehaviors) {
        finalTargeting.exclusions.behaviors = finalTargeting.excluded_behaviors;
      }
      if (hasExcludedInterests) {
        finalTargeting.exclusions.interests = finalTargeting.excluded_interests;
      }
    }
    delete finalTargeting.excluded_behaviors;
    delete finalTargeting.excluded_interests;

    // Behaviors mapping (Meta API expects behaviors to be inside flexible_spec)
    if (finalTargeting.behaviors && finalTargeting.behaviors.length > 0) {
      finalTargeting.flexible_spec = finalTargeting.flexible_spec || [];
      finalTargeting.flexible_spec.push({ behaviors: finalTargeting.behaviors });
    }
    delete finalTargeting.behaviors;

    // Clean up all empty arrays to prevent Meta API validation errors
    for (const key of Object.keys(finalTargeting)) {
      if (Array.isArray(finalTargeting[key]) && finalTargeting[key].length === 0) {
        delete finalTargeting[key];
      }
    }

    onSave(finalTargeting);
    onClose();
  };

  const toggleCountry = (countryCode: string) => {
    const current = targeting.geo_locations.countries || [];
    if (current.includes(countryCode)) {
      setTargeting({
        ...targeting,
        geo_locations: {
          ...targeting.geo_locations,
          countries: current.filter(c => c !== countryCode),
        },
      });
    } else {
      setTargeting({
        ...targeting,
        geo_locations: {
          ...targeting.geo_locations,
          countries: [...current, countryCode],
        },
      });
    }
  };

  const toggleLanguage = (locale: string) => {
    const current = targeting.locales || [];
    if (current.includes(locale)) {
      setTargeting({
        ...targeting,
        locales: current.filter(l => l !== locale),
      });
    } else {
      setTargeting({
        ...targeting,
        locales: [...current, locale],
      });
    }
  };

  const toggleGender = (genderValue: number) => {
    const current = targeting.genders || [0];
    if (current.includes(genderValue)) {
      const filtered = current.filter(g => g !== genderValue);
      setTargeting({
        ...targeting,
        genders: filtered.length === 0 ? [0] : filtered,
      });
    } else {
      setTargeting({
        ...targeting,
        genders: [...current.filter(g => g !== 0), genderValue],
      });
    }
  };

  const addCity = (city: any) => {
    const cities = targeting.geo_locations.cities || [];
    if (!cities.find(c => c.key === city.key)) {
      setTargeting({
        ...targeting,
        geo_locations: {
          ...targeting.geo_locations,
          cities: [...cities, {
            ...city,
            radius: selectedRadius,
            distance_unit: 'kilometer' as const,
          }],
        },
      });
    }
  };

  const removeCity = (cityKey: string) => {
    const cities = targeting.geo_locations.cities || [];
    setTargeting({
      ...targeting,
      geo_locations: {
        ...targeting.geo_locations,
        cities: cities.filter(c => c.key !== cityKey),
      },
    });
  };

  const updateCityRadius = (cityKey: string, radius: number) => {
    const cities = targeting.geo_locations.cities || [];
    setTargeting({
      ...targeting,
      geo_locations: {
        ...targeting.geo_locations,
        cities: cities.map(c => c.key === cityKey ? { ...c, radius } : c),
      },
    });
  };

  const toggleInterest = (interest: typeof POPULAR_INTERESTS[0]) => {
    const current = targeting.interests || [];
    if (current.find(i => i.name === interest.name)) {
      setTargeting({
        ...targeting,
        interests: current.filter(i => i.name !== interest.name),
      });
    } else {
      setTargeting({
        ...targeting,
        interests: [...current, interest],
      });
    }
  };

  const toggleBehavior = (behavior: typeof POPULAR_BEHAVIORS[0]) => {
    const current = targeting.behaviors || [];
    if (current.find(b => b.name === behavior.name)) {
      setTargeting({
        ...targeting,
        behaviors: current.filter(b => b.name !== behavior.name),
      });
    } else {
      setTargeting({
        ...targeting,
        behaviors: [...current, behavior],
      });
    }
  };

  const toggleExcludedInterest = (interest: typeof POPULAR_INTERESTS[0]) => {
    const current = targeting.excluded_interests || [];
    if (current.find(i => i.name === interest.name)) {
      setTargeting({
        ...targeting,
        excluded_interests: current.filter(i => i.name !== interest.name),
      });
    } else {
      setTargeting({
        ...targeting,
        excluded_interests: [...current, interest],
      });
    }
  };

  const toggleExcludedBehavior = (behavior: typeof POPULAR_BEHAVIORS[0]) => {
    const current = targeting.excluded_behaviors || [];
    if (current.find(b => b.name === behavior.name)) {
      setTargeting({
        ...targeting,
        excluded_behaviors: current.filter(b => b.name !== behavior.name),
      });
    } else {
      setTargeting({
        ...targeting,
        excluded_behaviors: [...current, behavior],
      });
    }
  };

  const toggleDevicePlatform = (platform: string) => {
    const current = targeting.device_platforms || [];
    if (current.includes(platform)) {
      setTargeting({
        ...targeting,
        device_platforms: current.filter(p => p !== platform),
      });
    } else {
      setTargeting({
        ...targeting,
        device_platforms: [...current, platform],
      });
    }
  };

  const toggleFacebookPosition = (position: string) => {
    const current = targeting.facebook_positions || [];
    if (current.includes(position)) {
      setTargeting({
        ...targeting,
        facebook_positions: current.filter(p => p !== position),
      });
    } else {
      setTargeting({
        ...targeting,
        facebook_positions: [...current, position],
      });
    }
  };

  const toggleInstagramPosition = (position: string) => {
    const current = targeting.instagram_positions || [];
    if (current.includes(position)) {
      setTargeting({
        ...targeting,
        instagram_positions: current.filter(p => p !== position),
      });
    } else {
      setTargeting({
        ...targeting,
        instagram_positions: [...current, position],
      });
    }
  };

  const availableCities = INTERNATIONAL_CITIES[selectedCountry as keyof typeof INTERNATIONAL_CITIES] || [];
  const filteredCities = availableCities.filter(city =>
    city.name.toLowerCase().includes(citySearch.toLowerCase())
  );

  const handleCustomAudienceUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const audienceId = `custom_${Date.now()}`;
      const audienceName = file.name.replace('.csv', '');

      setTargeting({
        ...targeting,
        custom_audiences: [
          ...(targeting.custom_audiences || []),
          { id: audienceId, name: audienceName }
        ],
      });
    };
    reader.readAsText(file);
  };

  const removeCustomAudience = (id: string) => {
    setTargeting({
      ...targeting,
      custom_audiences: (targeting.custom_audiences || []).filter(a => a.id !== id),
    });
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 rounded-xl border border-slate-700 max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-700 bg-slate-900">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Target className="w-5 h-5" />
              Profesjonalne Targetowanie - Faza {phase}
            </h2>
            <p className="text-sm text-slate-400 mt-1">
              Pełna kontrola zgodna z Meta Ads Manager
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-700 bg-slate-900/50 px-6">
          <button
            onClick={() => setActiveTab('basic')}
            className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
              activeTab === 'basic'
                ? 'border-blue-500 text-blue-400'
                : 'border-transparent text-slate-400 hover:text-slate-300'
            }`}
          >
            Podstawowe
          </button>
          <button
            onClick={() => setActiveTab('advanced')}
            className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
              activeTab === 'advanced'
                ? 'border-blue-500 text-blue-400'
                : 'border-transparent text-slate-400 hover:text-slate-300'
            }`}
          >
            Zaawansowane
          </button>
          <button
            onClick={() => setActiveTab('exclusions')}
            className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
              activeTab === 'exclusions'
                ? 'border-blue-500 text-blue-400'
                : 'border-transparent text-slate-400 hover:text-slate-300'
            }`}
          >
            Wykluczenia
          </button>
          <button
            onClick={() => setActiveTab('placement')}
            className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
              activeTab === 'placement'
                ? 'border-blue-500 text-blue-400'
                : 'border-transparent text-slate-400 hover:text-slate-300'
            }`}
          >
            Umiejscowienie
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'basic' && (
            <div className="space-y-6">
              {/* Age & Gender */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-3 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Zakres wiekowy
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Od</label>
                      <input
                        type="number"
                        min="13"
                        max="65"
                        value={targeting.age_min}
                        onChange={(e) => setTargeting({ ...targeting, age_min: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Do</label>
                      <input
                        type="number"
                        min="13"
                        max="65"
                        value={targeting.age_max}
                        onChange={(e) => setTargeting({ ...targeting, age_max: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-3 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Płeć
                  </label>
                  <div className="flex gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={targeting.genders.includes(0)}
                        onChange={() => {
                          if (targeting.genders.includes(0)) {
                            setTargeting({ ...targeting, genders: [] });
                          } else {
                            setTargeting({ ...targeting, genders: [0] });
                          }
                        }}
                        className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-slate-300">Wszyscy</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={targeting.genders.includes(1)}
                        onChange={() => toggleGender(1)}
                        disabled={targeting.genders.includes(0)}
                        className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                      />
                      <span className="text-sm text-slate-300">Mężczyźni</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={targeting.genders.includes(2)}
                        onChange={() => toggleGender(2)}
                        disabled={targeting.genders.includes(0)}
                        className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-pink-600 focus:ring-pink-500 disabled:opacity-50"
                      />
                      <span className="text-sm text-slate-300">Kobiety</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Countries */}
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Kraje
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {COUNTRIES.map(country => (
                    <label
                      key={country.code}
                      className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                        targeting.geo_locations.countries?.includes(country.code)
                          ? 'bg-blue-500/20 border-blue-500/50 text-blue-200'
                          : 'bg-slate-800 border-slate-700 text-slate-300 hover:border-slate-600'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={targeting.geo_locations.countries?.includes(country.code)}
                        onChange={() => toggleCountry(country.code)}
                        className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm">{country.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Cities */}
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Precyzyjna lokalizacja (miasta + promień)
                </label>

                <div className="mb-3 flex gap-2">
                  <select
                    value={selectedCountry}
                    onChange={(e) => {
                      setSelectedCountry(e.target.value);
                      setCitySearch('');
                    }}
                    className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="PL">Polska</option>
                    <option value="US">USA</option>
                    <option value="GB">UK</option>
                    <option value="DE">Niemcy</option>
                  </select>
                  <input
                    type="text"
                    placeholder="Szukaj miasta..."
                    value={citySearch}
                    onChange={(e) => setCitySearch(e.target.value)}
                    className="flex-1 px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <select
                    value={selectedRadius}
                    onChange={(e) => setSelectedRadius(parseInt(e.target.value))}
                    className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="10">10 km</option>
                    <option value="25">25 km</option>
                    <option value="50">50 km</option>
                    <option value="80">80 km</option>
                  </select>
                </div>

                {citySearch && (
                  <div className="mb-3 max-h-32 overflow-y-auto bg-slate-800 border border-slate-600 rounded-lg">
                    {filteredCities.map(city => (
                      <button
                        key={city.key}
                        onClick={() => {
                          addCity(city);
                          setCitySearch('');
                        }}
                        className="w-full px-3 py-2 text-left text-sm text-slate-300 hover:bg-slate-700 transition-colors"
                      >
                        <Plus className="w-3 h-3 inline mr-2" />
                        {city.name}
                      </button>
                    ))}
                    {filteredCities.length === 0 && citySearch.trim() !== '' && (
                      <button
                        onClick={() => {
                          addCity({
                            key: `custom_city_${citySearch}`,
                            name: citySearch,
                            country_code: selectedCountry,
                          });
                          setCitySearch('');
                        }}
                        className="w-full px-3 py-2 text-left text-sm text-blue-400 hover:bg-slate-700 transition-colors"
                      >
                        <Plus className="w-3 h-3 inline mr-2" />
                        Dodaj własne miasto: "{citySearch}"
                      </button>
                    )}
                  </div>
                )}

                {targeting.geo_locations.cities && targeting.geo_locations.cities.length > 0 && (
                  <div className="space-y-2">
                    {targeting.geo_locations.cities.map(city => (
                      <div key={city.key} className="flex items-center gap-2 p-3 bg-slate-800 border border-slate-600 rounded-lg">
                        <MapPin className="w-4 h-4 text-blue-400" />
                        <span className="flex-1 text-sm text-slate-300">{city.name}</span>
                        <input
                          type="number"
                          min="1"
                          max="80"
                          value={city.radius || 25}
                          onChange={(e) => updateCityRadius(city.key, parseInt(e.target.value))}
                          className="w-20 px-2 py-1 bg-slate-700 border border-slate-600 rounded text-white text-xs text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="text-xs text-slate-400">km</span>
                        <button
                          onClick={() => removeCity(city.key)}
                          className="p-1 hover:bg-slate-700 rounded transition-colors"
                        >
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Languages */}
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-3 flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  Języki
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {LANGUAGES.map(lang => (
                    <label
                      key={lang.code}
                      className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                        targeting.locales?.includes(lang.code)
                          ? 'bg-green-500/20 border-green-500/50 text-green-200'
                          : 'bg-slate-800 border-slate-700 text-slate-300 hover:border-slate-600'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={targeting.locales?.includes(lang.code)}
                        onChange={() => toggleLanguage(lang.code)}
                        className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-green-600 focus:ring-green-500"
                      />
                      <span className="text-sm">{lang.name}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-slate-400 mt-2">
                  Targetuj osoby używające wybranych języków na Facebooku
                </p>
              </div>
            </div>
          )}

          {activeTab === 'advanced' && (
            <div className="space-y-6">
              {/* Interests */}
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-3 flex items-center gap-2">
                  <Heart className="w-4 h-4" />
                  Zainteresowania
                </label>
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Wpisz dowolne zainteresowanie (np. Kosmetyki, Marketing, Sport)..."
                    value={interestSearch}
                    onChange={(e) => setInterestSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && interestSearch.trim() !== '') {
                        e.preventDefault();
                        toggleInterest({
                          id: `custom_interest_${interestSearch.trim()}`,
                          name: interestSearch.trim()
                        });
                        setInterestSearch('');
                      }
                    }}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  <p className="text-xs text-slate-500 mt-2">Naciśnij Enter, aby dodać własne zainteresowanie. Zostanie ono zmapowane automatycznie przez nasz serwer Meta API w tle.</p>
                </div>

                {/* Selected Custom Interests */}
                {targeting.interests && targeting.interests.filter(i => i.id.startsWith('custom_interest_')).length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {targeting.interests.filter(i => i.id.startsWith('custom_interest_')).map(interest => (
                      <span key={interest.id} className="px-3 py-1 bg-purple-500/20 border border-purple-500/50 text-purple-200 text-sm rounded-full flex items-center gap-2">
                        {interest.name}
                        <button onClick={() => toggleInterest(interest)} className="hover:text-white"><X className="w-3 h-3" /></button>
                      </span>
                    ))}
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {POPULAR_INTERESTS.map(interest => (
                    <label
                      key={interest.id + interest.name}
                      className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                        targeting.interests?.find(i => i.name === interest.name)
                          ? 'bg-purple-500/20 border-purple-500/50 text-purple-200'
                          : 'bg-slate-800 border-slate-700 text-slate-300 hover:border-slate-600'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={!!targeting.interests?.find(i => i.name === interest.name)}
                        onChange={() => toggleInterest(interest)}
                        className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="text-sm">{interest.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Behaviors */}
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-3 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Zachowania
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {POPULAR_BEHAVIORS.map(behavior => (
                    <label
                      key={behavior.id + behavior.name}
                      className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                        targeting.behaviors?.find(b => b.name === behavior.name)
                          ? 'bg-green-500/20 border-green-500/50 text-green-200'
                          : 'bg-slate-800 border-slate-700 text-slate-300 hover:border-slate-600'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={!!targeting.behaviors?.find(b => b.name === behavior.name)}
                        onChange={() => toggleBehavior(behavior)}
                        className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-green-600 focus:ring-green-500"
                      />
                      <span className="text-sm">{behavior.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Custom Audiences */}
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-3 flex items-center gap-2">
                  <Upload className="w-4 h-4" />
                  Custom Audiences (CSV)
                </label>
                <div className="p-4 bg-slate-800 border border-slate-600 rounded-lg">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleCustomAudienceUpload}
                    className="hidden"
                    id="csv-upload"
                  />
                  <label
                    htmlFor="csv-upload"
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg cursor-pointer transition-colors"
                  >
                    <Upload className="w-4 h-4" />
                    Wgraj listę klientów (CSV)
                  </label>
                  <p className="text-xs text-slate-400 mt-2">
                    Format: email,phone,first_name,last_name (jeden na linię)
                  </p>
                </div>

                {targeting.custom_audiences && targeting.custom_audiences.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {targeting.custom_audiences.map(audience => (
                      <div key={audience.id} className="flex items-center justify-between p-3 bg-slate-800 border border-slate-600 rounded-lg">
                        <span className="text-sm text-slate-300">{audience.name}</span>
                        <button
                          onClick={() => removeCustomAudience(audience.id)}
                          className="p-1 hover:bg-slate-700 rounded transition-colors"
                        >
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'exclusions' && (
            <div className="space-y-6">
              <div className="p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                <p className="text-sm text-orange-300">
                  <Ban className="w-4 h-4 inline mr-2" />
                  Wykluczenia pozwalają uniknąć wyświetlania reklam określonym grupom
                </p>
              </div>

              {/* Excluded Interests */}
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-3 flex items-center gap-2">
                  <Ban className="w-4 h-4" />
                  Wyklucz zainteresowania
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {POPULAR_INTERESTS.map(interest => (
                    <label
                      key={interest.id + interest.name}
                      className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                        targeting.excluded_interests?.find(i => i.name === interest.name)
                          ? 'bg-red-500/20 border-red-500/50 text-red-200'
                          : 'bg-slate-800 border-slate-700 text-slate-300 hover:border-slate-600'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={!!targeting.excluded_interests?.find(i => i.name === interest.name)}
                        onChange={() => toggleExcludedInterest(interest)}
                        className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-red-600 focus:ring-red-500"
                      />
                      <span className="text-sm">{interest.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Excluded Behaviors */}
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-3 flex items-center gap-2">
                  <Ban className="w-4 h-4" />
                  Wyklucz zachowania
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {POPULAR_BEHAVIORS.map(behavior => (
                    <label
                      key={behavior.id + behavior.name}
                      className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                        targeting.excluded_behaviors?.find(b => b.name === behavior.name)
                          ? 'bg-red-500/20 border-red-500/50 text-red-200'
                          : 'bg-slate-800 border-slate-700 text-slate-300 hover:border-slate-600'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={!!targeting.excluded_behaviors?.find(b => b.name === behavior.name)}
                        onChange={() => toggleExcludedBehavior(behavior)}
                        className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-red-600 focus:ring-red-500"
                      />
                      <span className="text-sm">{behavior.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'placement' && (
            <div className="space-y-6">
              {/* Device Platforms */}
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-3 flex items-center gap-2">
                  <Smartphone className="w-4 h-4" />
                  Urządzenia
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {DEVICE_PLATFORMS.map(device => (
                    <label
                      key={device.value}
                      className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                        targeting.device_platforms?.includes(device.value)
                          ? 'bg-blue-500/20 border-blue-500/50 text-blue-200'
                          : 'bg-slate-800 border-slate-700 text-slate-300 hover:border-slate-600'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={targeting.device_platforms?.includes(device.value)}
                        onChange={() => toggleDevicePlatform(device.value)}
                        className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm">{device.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Facebook Positions */}
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-3 flex items-center gap-2">
                  <Layout className="w-4 h-4" />
                  Umiejscowienia Facebook
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {FACEBOOK_POSITIONS.map(position => (
                    <label
                      key={position.value}
                      className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                        targeting.facebook_positions?.includes(position.value)
                          ? 'bg-blue-500/20 border-blue-500/50 text-blue-200'
                          : 'bg-slate-800 border-slate-700 text-slate-300 hover:border-slate-600'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={targeting.facebook_positions?.includes(position.value)}
                        onChange={() => toggleFacebookPosition(position.value)}
                        className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm">{position.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Instagram Positions */}
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-3 flex items-center gap-2">
                  <Layout className="w-4 h-4" />
                  Umiejscowienia Instagram
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {INSTAGRAM_POSITIONS.map(position => (
                    <label
                      key={position.value}
                      className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                        targeting.instagram_positions?.includes(position.value)
                          ? 'bg-pink-500/20 border-pink-500/50 text-pink-200'
                          : 'bg-slate-800 border-slate-700 text-slate-300 hover:border-slate-600'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={targeting.instagram_positions?.includes(position.value)}
                        onChange={() => toggleInstagramPosition(position.value)}
                        className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-pink-600 focus:ring-pink-500"
                      />
                      <span className="text-sm">{position.label}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-slate-400 mt-2">
                  Jeśli nic nie wybierzesz, Meta automatycznie optymalizuje umiejscowienia
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-slate-700 bg-slate-900/50">
          <div className="text-xs text-slate-400 space-y-1">
            <div>
              {targeting.geo_locations.countries?.length || 0} krajów • {targeting.geo_locations.cities?.length || 0} miast
              {targeting.locales && targeting.locales.length > 0 && ` • ${targeting.locales.length} języków`}
            </div>
            <div>
              {targeting.interests?.length || 0} zainteresowań • {targeting.behaviors?.length || 0} zachowań
              {targeting.excluded_interests && targeting.excluded_interests.length > 0 && ` • ${targeting.excluded_interests.length} wykluczeń`}
            </div>
            {targeting.custom_audiences && targeting.custom_audiences.length > 0 && (
              <div>{targeting.custom_audiences.length} custom audiences</div>
            )}
          </div>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-slate-300 hover:text-white transition-colors"
            >
              Anuluj
            </button>
            <button
              onClick={handleSave}
              className="flex items-center gap-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
            >
              <Save className="w-4 h-4" />
              Zapisz Targetowanie
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
